import { AddressList } from '@qbe/dfc-services';
import { AddressMethod, AddressSearchValue } from '@qbe/dfc-smart-ui';
import { useCallback, useEffect } from 'react';

import { useAppDispatch, useBusinessDetails } from '~/hooks';
import { useManualAddressValidationMutation } from '~/services/address';
import {
  clearLookedupAddress,
  clearManualAddress,
  clearPerils,
  updateAddressLine1,
  updateBusinessAddress,
  updateFullBusinessAddress,
  updateIsManualEntry,
  updateIsValidAddress,
  updateIsVerifiedAddress,
  updatePerils,
  updatePostcode,
  updateState,
  updateSuburb,
} from '~/slices/businessDetails';
import { PerilLevel, Perils, RiskRatingResponse } from '~/types/address';
import { transformRiskRatingResponse, utcDateFormatter } from '~/utils';

interface UseAddressHandlersProps {
  onLoadingChange?: (isLoading: boolean) => void;
}

interface UseAddressHandlersReturn {
  handleAddressChange: (
    addressSearchValue: AddressList | null,
    structuredAddress: AddressSearchValue | null,
    fieldName: string,
    addressMethod: AddressMethod
  ) => void;
  handleAddressError: (hasError: boolean) => void;
  handleRiskRatingChange: (value: unknown) => void;
  handleRiskRatingError: (hasError: boolean) => void;
  handleRiskRatingLoadingChange: () => void;
  manualAddressData: ReturnType<typeof useManualAddressValidationMutation>[1]['data'];
  manualAddressError: ReturnType<typeof useManualAddressValidationMutation>[1]['error'];
  isError: boolean;
  isWarning: boolean;
}

export const useAddressHandlers = ({
  onLoadingChange,
}: UseAddressHandlersProps = {}): UseAddressHandlersReturn => {
  const dispatch = useAppDispatch();
  const { coverStart } = useBusinessDetails();
  const [
    manualAddressValidation,
    { data: manualAddressData, error: manualAddressError, reset: resetManualAddressValidation },
  ] = useManualAddressValidationMutation();

  // Update Redux when manual address data is received
  useEffect(() => {
    if (manualAddressData) {
      const isValidAddress = manualAddressData.address.isValid;
      const isVerifiedAddress = manualAddressData.address.isVerified;

      dispatch(updateIsValidAddress(isValidAddress));
      dispatch(updateIsVerifiedAddress(isVerifiedAddress));
    }
  }, [manualAddressData, dispatch]);

  // Derive error/warning states directly from data
  const isError = manualAddressData ? !manualAddressData.address.isValid : false;
  const isWarning = manualAddressData
    ? manualAddressData.address.isValid && !manualAddressData.address.isVerified
    : false;

  /**
   * Handles address changes from AddressSearch component.
   * Manages both lookup (autocomplete) and manual entry flows.
   */
  const handleAddressChange = useCallback(
    (
      addressSearchValue: AddressList | null,
      structuredAddress: AddressSearchValue | null,
      fieldName: string,
      addressMethod: AddressMethod
    ) => {
      // Clear all address-related state if user clears the field
      if (!addressSearchValue && !structuredAddress) {
        resetManualAddressValidation();
        dispatch(updateBusinessAddress(undefined));
        dispatch(updateFullBusinessAddress(undefined));
        dispatch(updateIsManualEntry(false));
        dispatch(updateIsValidAddress(false));
        dispatch(updateIsVerifiedAddress(false));
        dispatch(clearManualAddress());
        dispatch(clearLookedupAddress());
        dispatch(clearPerils());
        return;
      }

      // Handle address lookup (autocomplete selection)
      if (addressMethod === AddressMethod.Lookup) {
        // Reset manual address validation state when switching to lookup
        resetManualAddressValidation();

        // Store the dropdown item (moniker, partialAddress, picklist)
        dispatch(updateBusinessAddress(addressSearchValue ?? undefined));

        // Map structuredAddress fields to the AddressMonikerResponse shape (state → stateCode, postcode → postalCode)
        dispatch(
          updateFullBusinessAddress(
            structuredAddress
              ? {
                  buildingName: structuredAddress.buildingName ?? '',
                  streetAddress1: structuredAddress.streetAddress1,
                  city: structuredAddress.city,
                  stateCode: structuredAddress.state,
                  postalCode: structuredAddress.postcode,
                }
              : undefined
          )
        );

        dispatch(updateIsManualEntry(false));
      }

      // Handle manual address entry
      if (addressMethod === AddressMethod.Manual && structuredAddress) {
        const {
          streetAddress1,
          city,
          state: addrState,
          postcode: addrPostcode,
        } = structuredAddress;

        dispatch(updateAddressLine1(streetAddress1));
        dispatch(updateSuburb(city));
        dispatch(updateState(addrState));
        dispatch(updatePostcode(addrPostcode));
        dispatch(updateIsManualEntry(true));

        // Validate the manually entered address
        manualAddressValidation({
          address: {
            street: streetAddress1,
            suburb: city,
            state: addrState,
            postcode: addrPostcode,
            effectiveDate: utcDateFormatter(coverStart!),
            perilLevel: PerilLevel.STREET,
          },
        });
      }
    },
    [dispatch, manualAddressValidation, coverStart, resetManualAddressValidation]
  );

  const handleAddressError = useCallback(
    (_hasError: boolean) => {
      dispatch(clearLookedupAddress());
    },
    [dispatch]
  );

  /**
   * Processes risk rating response and updates Redux with peril data.
   * Transforms DFC risk rating format to DigiSME Perils shape.
   */
  const handleRiskRatingChange = useCallback(
    (value: unknown) => {
      onLoadingChange?.(false);

      if (value && typeof value === 'object' && 'riskDetails' in value) {
        const riskRatingValue = value as RiskRatingResponse;
        const perils = transformRiskRatingResponse(riskRatingValue);
        dispatch(updatePerils(perils as Perils));
      }
    },
    [dispatch, onLoadingChange]
  );

  const handleRiskRatingError = useCallback(
    (_hasError: boolean) => {
      onLoadingChange?.(false);
    },
    [onLoadingChange]
  );

  const handleRiskRatingLoadingChange = useCallback(() => {
    onLoadingChange?.(true);
  }, [onLoadingChange]);

  return {
    handleAddressChange,
    handleAddressError,
    handleRiskRatingChange,
    handleRiskRatingError,
    handleRiskRatingLoadingChange,
    manualAddressData,
    manualAddressError,
    isError,
    isWarning,
  };
};
